import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import servicesService from "../services/ServicesService";
import { errorMessage } from "../shared/utils/validators";
import FormField from "../components/FormField";
import PERSONA_CHARACTER_PRESETS from "../constants/personaCharacterPresets";

const PERSONA_LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "ta", label: "Tamil" },
  { value: "hi", label: "Hindi" },
  { value: "te", label: "Telugu" },
  { value: "kn", label: "Kannada" },
];

function AiPersonaListPage({
  activeProviderLabel,
  isActiveProviderConfigured,
  aiForm,
  setAiForm,
  providerMeta,
  businessSetup,
  hasRequiredBusinessSetup,
  businessAddressText,
  handleSaveProviders,
  handleDeleteProvider,
  savingProvider,
  filteredPersonas,
  setPersonaToDelete,
  usageSummary,
}) {
  const navigate = useNavigate();
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        AI Assistant Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Configure a single AI provider and manage reusable persona presets for automated conversations.
      </Typography>

      <Box
        sx={{
          mb: 2,
          p: 2.25,
          border: "1px solid #e6e9ef",
          borderRadius: 2,
          background: "linear-gradient(180deg, #fafcff 0%, #f7f9fc 100%)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">Active provider</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{activeProviderLabel}</Typography>
        </Box>
        <Box
          sx={{
            px: 1.25, py: 0.5, borderRadius: 10, fontSize: 12, fontWeight: 700,
            color: isActiveProviderConfigured ? "#1B5E20" : "#B26A00",
            backgroundColor: isActiveProviderConfigured ? "#E8F5E9" : "#FFF3E0",
          }}
        >
          {isActiveProviderConfigured ? "Configured" : "Not Configured"}
        </Box>
      </Box>

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pb: 1.5 }}>
        <Button variant="outlined" onClick={() => setProviderDialogOpen(true)}>
          Provider Settings
        </Button>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            AI Usage (last {usageSummary?.days || 14} days)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tokens + estimated cost
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 1.5 }}>
          <Chip label={`Replies: ${Number(usageSummary?.totals?.reply_count || 0).toLocaleString()}`} />
          <Chip label={`Total tokens: ${Number(usageSummary?.totals?.total_tokens || 0).toLocaleString()}`} />
          <Chip label={`Prompt: ${Number(usageSummary?.totals?.prompt_tokens || 0).toLocaleString()}`} variant="outlined" />
          <Chip label={`Completion: ${Number(usageSummary?.totals?.completion_tokens || 0).toLocaleString()}`} variant="outlined" />
          <Chip label={`Est. cost: $${Number(usageSummary?.totals?.estimated_cost || 0).toFixed(4)}`} color="secondary" />
        </Stack>
        <Stack spacing={0.75}>
          {(usageSummary?.timeline || []).slice(-10).map((row) => {
            const max = Math.max(...(usageSummary?.timeline || []).map((r) => Number(r.total_tokens || 0)), 1);
            const pct = Math.min(100, Math.round((Number(row.total_tokens || 0) / max) * 100));
            return (
              <Box key={`${row.day}-${row.provider_key}-${row.provider_model}`} sx={{ display: "grid", gridTemplateColumns: "120px 1fr 130px", gap: 1, alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">{row.day}</Typography>
                <Box sx={{ height: 10, borderRadius: 10, backgroundColor: "#eef2ff", overflow: "hidden" }}>
                  <Box sx={{ width: `${pct}%`, height: "100%", backgroundColor: "#4f7cff" }} />
                </Box>
                <Typography variant="caption" color="text.secondary" textAlign="right">
                  {Number(row.total_tokens || 0).toLocaleString()} tk
                </Typography>
              </Box>
            );
          })}
          {(usageSummary?.timeline || []).length === 0 && (
            <Typography variant="body2" color="text.secondary">No usage yet.</Typography>
          )}
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Persona Presets ({activeProviderLabel})
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/ai-chatbot/personas/new")}
          disabled={!isActiveProviderConfigured}
        >
          New Persona Preset
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
              <TableCell sx={{ fontWeight: 700 }}>Preset ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Character</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 180 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPersonas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No personas created for this provider yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredPersonas.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.persona_key || p.id}</TableCell>
                <TableCell>{p.title}</TableCell>
                <TableCell>{p.character}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={String(p.provider_key || "").toLowerCase() === "gemini" ? "Gemini" : "OpenAI"}
                    color={String(p.provider_key || "").toLowerCase() === "gemini" ? "secondary" : "primary"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => navigate(`/ai-chatbot/personas/${p.id}/edit`)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" variant="text" onClick={() => setPersonaToDelete(p)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!isActiveProviderConfigured && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Save provider configuration first to create personas.
        </Typography>
      )}

      <Dialog open={providerDialogOpen} onClose={() => setProviderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Provider Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>AI provider</InputLabel>
              <Select
                label="AI provider"
                value={aiForm.selected_provider_key}
                onChange={(e) => {
                  const nextProviderKey = String(e.target.value || "openai").toLowerCase();
                  const nextProviderMeta = nextProviderKey === "gemini" ? providerMeta.gemini : providerMeta.openai;
                  setAiForm((p) => ({
                    ...p,
                    selected_provider_key: nextProviderKey,
                    provider_api_key: nextProviderMeta.apiKey || "",
                    provider_model: nextProviderMeta.model || "",
                  }));
                }}
              >
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="gemini">Gemini</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mt: 2 }} />
            <FormField
              type="text"
              name="provider_api_key"
              label={`${aiForm.selected_provider_key === "gemini" ? "Gemini" : "OpenAI"} API key`}
              value={aiForm.provider_api_key}
              onChange={(e) => setAiForm((p) => ({ ...p, provider_api_key: e.target.value }))}
              instruction="API key is editable. Update and save to rotate."
            />
            <Box sx={{ mt: 2 }} />
            <FormField
              type="text"
              name="provider_model"
              label={`${aiForm.selected_provider_key === "gemini" ? "Gemini" : "OpenAI"} model`}
              value={aiForm.provider_model}
              isRequired
              onChange={(e) => setAiForm((p) => ({ ...p, provider_model: e.target.value }))}
              instruction={aiForm.selected_provider_key === "gemini"
                ? "Example: gemini-1.5-flash"
                : "Example: gpt-4o-mini"}
            />
            <Box sx={{ mt: 2 }} />
            {hasRequiredBusinessSetup ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                AI replies will use Business Setup details:
                {" "}
                <b>{businessSetup.businessName}</b>
                {businessSetup.businessPhone ? `, ${businessSetup.businessPhone}` : ""}
                {businessAddressText ? `, ${businessAddressText}` : ""}.
              </Alert>
            ) : (
              <Alert
                severity="warning"
                sx={{ borderRadius: 2 }}
                action={(
                  <Button color="inherit" size="small" onClick={() => navigate("/dashboard")}>
                    Open Business Setup
                  </Button>
                )}
              >
                Business info is required before configuring AI provider settings. Add business
                name, phone number, and address in Dashboard → Business Setup.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="error"
            onClick={async () => {
              const providerLabel = aiForm.selected_provider_key === "gemini" ? "Gemini" : "OpenAI";
              const confirmed = window.confirm(
                `Delete ${providerLabel} provider configuration?\n\nThis will also delete all persona presets and related AI auto-reply data for this provider.`
              );
              if (!confirmed) return;
              const ok = await handleDeleteProvider(aiForm.selected_provider_key);
              if (ok) setProviderDialogOpen(false);
            }}
          >
            Delete Provider
          </Button>
          <Button onClick={() => setProviderDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const ok = await handleSaveProviders();
              if (ok) setProviderDialogOpen(false);
            }}
            disabled={savingProvider || !hasRequiredBusinessSetup}
          >
            {savingProvider ? "Saving..." : "Save / Rotate Keys"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AiPersonaFormPage({
  mode,
  activeProviderLabel,
  selectedProviderConfigId,
  filteredPersonas,
  setSnackbar,
  syncFromAiTables,
}) {
  const navigate = useNavigate();
  const { personaId } = useParams();
  const editingPersona = mode === "edit" ? filteredPersonas.find((p) => String(p.id) === String(personaId)) : null;
  const [saving, setSaving] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("custom");
  const [form, setForm] = useState({
    persona_id: editingPersona?.persona_key || "",
    persona_title: editingPersona?.title || "",
    persona_character: editingPersona?.character || "",
    system_prompt: editingPersona?.system_prompt || "",
    product_reply_policy: editingPersona?.product_reply_policy || "",
    off_topic_reply_policy: editingPersona?.off_topic_reply_policy || "",
    language_preferences: Array.isArray(editingPersona?.language_preferences)
      ? editingPersona.language_preferences
      : [],
    default_reply_language: editingPersona?.default_reply_language || "en",
    romanized_reply_mode: editingPersona?.romanized_reply_mode || "auto",
    max_products: Number(editingPersona?.max_products ?? 4),
    max_chars: Number(editingPersona?.max_chars ?? 1200),
    max_tokens: Number(editingPersona?.max_tokens ?? 400),
  });

  useEffect(() => {
    if (editingPersona) {
      setForm({
        persona_id: editingPersona.persona_key || "",
        persona_title: editingPersona.title || "",
        persona_character: editingPersona.character || "",
        system_prompt: editingPersona.system_prompt || "",
        product_reply_policy: editingPersona.product_reply_policy || "",
        off_topic_reply_policy: editingPersona.off_topic_reply_policy || "",
        language_preferences: Array.isArray(editingPersona.language_preferences)
          ? editingPersona.language_preferences
          : [],
        default_reply_language: editingPersona.default_reply_language || "en",
        romanized_reply_mode: editingPersona.romanized_reply_mode || "auto",
        max_products: Number(editingPersona.max_products ?? 4),
        max_chars: Number(editingPersona.max_chars ?? 1200),
        max_tokens: Number(editingPersona.max_tokens ?? 400),
      });
    }
  }, [editingPersona]);

  useEffect(() => {
    const currentCharacter = String(form.persona_character || "").toLowerCase();
    if (currentCharacter.includes("sales")) return setSelectedPreset("sales");
    if (currentCharacter.includes("support")) return setSelectedPreset("support");
    if (currentCharacter.includes("retention")) return setSelectedPreset("retention");
    setSelectedPreset("custom");
  }, [form.persona_character]);

  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    if (presetKey === "custom") return;
    const preset = PERSONA_CHARACTER_PRESETS[presetKey];
    if (!preset) return;
    setForm((prev) => ({
      ...prev,
      persona_title: preset.persona_title,
      persona_character: preset.persona_character,
      system_prompt: preset.system_prompt,
      product_reply_policy: preset.product_reply_policy,
      off_topic_reply_policy: preset.off_topic_reply_policy,
      max_products: preset.max_products,
      max_chars: preset.max_chars,
      max_tokens: preset.max_tokens,
    }));
  };

  const validatePersona = () => {
    const errs = [];
    const requiredString = (label, value) => {
      if (!String(value || "").trim()) errs.push(`${label} is required`);
    };
    requiredString("Persona id", form.persona_id);
    requiredString("Persona title", form.persona_title);
    requiredString("Persona character", form.persona_character);
    requiredString("System prompt", form.system_prompt);
    requiredString("Product reply policy", form.product_reply_policy);
    requiredString("Off-topic reply policy", form.off_topic_reply_policy);
    if (!Array.isArray(form.language_preferences) || form.language_preferences.length === 0) {
      errs.push("Select at least one language preference");
    }
    if (!String(form.default_reply_language || "").trim()) {
      errs.push("Default reply language is required");
    }
    const n1 = Number(form.max_products);
    const n2 = Number(form.max_chars);
    const n3 = Number(form.max_tokens);
    if (!Number.isFinite(n1) || n1 <= 0) errs.push("max_products must be a valid number > 0");
    if (!Number.isFinite(n2) || n2 <= 0) errs.push("max_chars must be a valid number > 0");
    if (!Number.isFinite(n3) || n3 <= 0) errs.push("max_tokens must be a valid number > 0");
    return errs;
  };

  const handleSavePersona = async () => {
    if (!selectedProviderConfigId) {
      setSnackbar({ open: true, message: "Save provider configuration first before creating persona.", severity: "error" });
      return;
    }
    const errs = validatePersona();
    if (errs.length > 0) {
      setSnackbar({ open: true, message: errs[0], severity: "error" });
      return;
    }
    setSaving(true);
    try {
      const personaPayload = {
        provider_config_id: selectedProviderConfigId,
        persona_key: form.persona_id,
        title: form.persona_title,
        character: form.persona_character,
        system_prompt: form.system_prompt,
        product_reply_policy: form.product_reply_policy,
        off_topic_reply_policy: form.off_topic_reply_policy,
        language_preferences: form.language_preferences,
        default_reply_language: form.default_reply_language,
        romanized_reply_mode: form.romanized_reply_mode,
        max_products: Number(form.max_products),
        max_chars: Number(form.max_chars),
        max_tokens: Number(form.max_tokens),
        channel_scope: "omnichannel",
        enabled: true,
      };
      if (mode === "edit" && editingPersona?.id) {
        await servicesService.updateAiPersona(editingPersona.id, personaPayload);
      } else {
        await servicesService.createAiPersona(personaPayload);
      }
      const [nextProviders, nextPersonas] = await Promise.all([
        servicesService.getAiProviderConfigs(),
        servicesService.getAiPersonas(),
      ]);
      syncFromAiTables({ providers: nextProviders, personas: nextPersonas });
      setSnackbar({
        open: true,
        message: mode === "edit" ? "Persona updated successfully" : "Persona created successfully",
        severity: "success",
      });
      navigate("/ai-chatbot");
    } catch (e) {
      setSnackbar({ open: true, message: errorMessage(e) || "Failed to save persona", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (mode === "edit" && !editingPersona) {
    return (
      <Box sx={{ maxWidth: 900 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Persona not found</Typography>
        <Button variant="outlined" onClick={() => navigate("/ai-chatbot")}>Back to persona list</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {mode === "edit" ? "Edit persona preset" : "Create persona preset"}
        </Typography>
        <Button variant="outlined" size="small" onClick={() => navigate("/ai-chatbot")} disabled={saving}>
          Back to Presets
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Provider:</Typography>
        <Chip size="small" label={activeProviderLabel} color={activeProviderLabel === "Gemini" ? "secondary" : "primary"} variant="outlined" />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: "#e6e9ef", mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 600 }}>Persona details</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Character preset</InputLabel>
          <Select label="Character preset" value={selectedPreset} onChange={(e) => handlePresetChange(e.target.value)}>
            <MenuItem value="custom">Custom</MenuItem>
            <MenuItem value="sales">{PERSONA_CHARACTER_PRESETS.sales.label}</MenuItem>
            <MenuItem value="support">{PERSONA_CHARACTER_PRESETS.support.label}</MenuItem>
            <MenuItem value="retention">{PERSONA_CHARACTER_PRESETS.retention.label}</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <FormField type="text" name="persona_id" label="Persona preset id" value={form.persona_id} isRequired onChange={(e) => setForm((p) => ({ ...p, persona_id: e.target.value }))} />
          <FormField type="text" name="persona_title" label="Bot title" value={form.persona_title} isRequired onChange={(e) => setForm((p) => ({ ...p, persona_title: e.target.value }))} />
        </Box>
        <FormField type="text" name="persona_character" label="Persona character" value={form.persona_character} isRequired onChange={(e) => setForm((p) => ({ ...p, persona_character: e.target.value }))} />
        <Box sx={{ mt: 2 }} />
        <FormControl fullWidth>
          <InputLabel>Preferred reply languages</InputLabel>
          <Select
            multiple
            label="Preferred reply languages"
            value={form.language_preferences}
            onChange={(e) => setForm((p) => ({ ...p, language_preferences: e.target.value }))}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {selected.map((code) => {
                  const row = PERSONA_LANGUAGE_OPTIONS.find((x) => x.value === code);
                  return <Chip key={code} size="small" label={row?.label || code} />;
                })}
              </Box>
            )}
          >
            {PERSONA_LANGUAGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          AI will reply in the customer language when detected among selected languages. Product and business details stay unchanged.
        </Typography>
        <Box sx={{ mt: 2 }} />
        <FormControl fullWidth>
          <InputLabel>Default reply language</InputLabel>
          <Select
            label="Default reply language"
            value={form.default_reply_language}
            onChange={(e) => setForm((p) => ({ ...p, default_reply_language: e.target.value }))}
          >
            {PERSONA_LANGUAGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          Used when customer language is unclear (for example, Romanized text like Tanglish/Hinglish).
        </Typography>
        <Box sx={{ mt: 2 }} />
        <FormControl fullWidth>
          <InputLabel>Romanized reply mode</InputLabel>
          <Select
            label="Romanized reply mode"
            value={form.romanized_reply_mode}
            onChange={(e) => setForm((p) => ({ ...p, romanized_reply_mode: e.target.value }))}
          >
            <MenuItem value="auto">Auto (detect Tanglish / Hinglish)</MenuItem>
            <MenuItem value="off">Off</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          Auto keeps the same Romanized style when customer types Tamil/Hindi in English letters.
        </Typography>
        <Box sx={{ mt: 2 }} />
        <FormField type="textarea" height={120} name="system_prompt" label="System prompt" value={form.system_prompt} isRequired onChange={(e) => setForm((p) => ({ ...p, system_prompt: e.target.value }))} />
        <Box sx={{ mt: 2 }} />
        <FormField type="textarea" height={110} name="product_reply_policy" label="Product reply policy" value={form.product_reply_policy} isRequired onChange={(e) => setForm((p) => ({ ...p, product_reply_policy: e.target.value }))} />
        <Box sx={{ mt: 2 }} />
        <FormField type="textarea" height={110} name="off_topic_reply_policy" label="Off-topic (other website) reply policy" value={form.off_topic_reply_policy} isRequired onChange={(e) => setForm((p) => ({ ...p, off_topic_reply_policy: e.target.value }))} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: "#e6e9ef", mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 600 }}>Response constraints</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
          <FormField type="number" name="max_products" label="max_products" value={form.max_products} isRequired onChange={(e) => setForm((p) => ({ ...p, max_products: e.target.value }))} />
          <FormField type="number" name="max_chars" label="max_chars" value={form.max_chars} isRequired onChange={(e) => setForm((p) => ({ ...p, max_chars: e.target.value }))} />
          <FormField type="number" name="max_tokens" label="max_tokens" value={form.max_tokens} isRequired onChange={(e) => setForm((p) => ({ ...p, max_tokens: e.target.value }))} />
        </Box>
      </Paper>

      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 0.5 }}>
        <Button variant="text" onClick={() => navigate("/ai-chatbot")} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSavePersona} disabled={saving}>
          {saving ? "Saving..." : mode === "edit" ? "Update Preset" : "Create Preset"}
        </Button>
      </Stack>
    </Box>
  );
}

export default function AiChatbotSettings() {
  const [loading, setLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [aiForm, setAiForm] = useState({
    selected_provider_key: "openai",
    provider_api_key: "",
    provider_model: "",
  });
  const [businessSetup, setBusinessSetup] = useState({});
  const [personas, setPersonas] = useState([]);
  const [usageSummary, setUsageSummary] = useState({ days: 14, timeline: [], totals: {} });
  const [personaToDelete, setPersonaToDelete] = useState(null);
  const [providerMeta, setProviderMeta] = useState({
    openai: { id: null, hasKey: false, apiKey: "", model: "" },
    gemini: { id: null, hasKey: false, apiKey: "", model: "" },
  });

  const syncFromAiTables = ({ providers = [], personas = [] }) => {
    const openai = (providers || []).find((p) => String(p.provider_key || "").toLowerCase() === "openai");
    const gemini = (providers || []).find((p) => String(p.provider_key || "").toLowerCase() === "gemini");
    const firstPersona = Array.isArray(personas) && personas.length > 0 ? personas[0] : {};
    const selectedProviderKey = String(
      firstPersona?.provider_key || (openai ? "openai" : gemini ? "gemini" : "openai")
    ).toLowerCase();

    setPersonas(Array.isArray(personas) ? personas : []);
    setProviderMeta({
      openai: {
        id: openai?.id || null,
        hasKey: !!openai?.has_api_key,
        apiKey: openai?.api_key || "",
        model: openai?.model || "",
      },
      gemini: {
        id: gemini?.id || null,
        hasKey: !!gemini?.has_api_key,
        apiKey: gemini?.api_key || "",
        model: gemini?.model || "",
      },
    });
    setAiForm((prev) => ({
      ...prev,
      selected_provider_key: selectedProviderKey,
      provider_api_key: selectedProviderKey === "gemini" ? (gemini?.api_key || "") : (openai?.api_key || ""),
      provider_model: selectedProviderKey === "gemini" ? (gemini?.model || "") : (openai?.model || ""),
    }));
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const [providers, personasData, usageData, settings] = await Promise.all([
          servicesService.getAiProviderConfigs(),
          servicesService.getAiPersonas(),
          servicesService.getAiUsageSummary(14),
          servicesService.getServiceSettings().catch(() => null),
        ]);
        if (isMounted) syncFromAiTables({ providers, personas: personasData });
        if (isMounted) setUsageSummary(usageData || { days: 14, timeline: [], totals: {} });
        if (isMounted) setBusinessSetup(settings?.allSettings?.business_setup || {});
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const businessAddressText = [
    businessSetup.businessAddressLine1,
    businessSetup.businessAddressLine2,
    businessSetup.businessCity,
    businessSetup.businessState,
    businessSetup.businessPincode,
    businessSetup.country,
  ].filter(Boolean).join(", ");
  const hasRequiredBusinessSetup = Boolean(
    String(businessSetup.businessName || "").trim()
      && String(businessSetup.businessPhone || "").trim()
      && String(businessSetup.businessAddressLine1 || "").trim()
      && String(businessSetup.businessCity || "").trim()
      && String(businessSetup.businessState || "").trim()
      && String(businessSetup.businessPincode || "").trim()
  );

  const handleSaveProviders = async () => {
    const selectedProviderKey = String(aiForm.selected_provider_key || "openai").toLowerCase();
    const selectedProviderLabel = selectedProviderKey === "gemini" ? "Gemini" : "OpenAI";
    const hasProviderKey = selectedProviderKey === "gemini" ? providerMeta.gemini.hasKey : providerMeta.openai.hasKey;
    if (!hasRequiredBusinessSetup) {
      setSnackbar({
        open: true,
        message: "Complete Business Setup with business name, phone, and address before configuring AI.",
        severity: "error",
      });
      return false;
    }
    if (!hasProviderKey && !String(aiForm.provider_api_key || "").trim()) {
      setSnackbar({ open: true, message: `${selectedProviderLabel} API key is required`, severity: "error" });
      return false;
    }
    if (!String(aiForm.provider_model || "").trim()) {
      setSnackbar({ open: true, message: `${selectedProviderLabel} model is required`, severity: "error" });
      return false;
    }

    setSavingProvider(true);
    try {
      await servicesService.upsertAiProviderConfig({
        provider_key: selectedProviderKey,
        config_name: "default",
        api_key: aiForm.provider_api_key,
        model: aiForm.provider_model,
        metadata: {},
        enabled: true,
      });
      const [providers, allPersonas, usageData] = await Promise.all([
        servicesService.getAiProviderConfigs(),
        servicesService.getAiPersonas(),
        servicesService.getAiUsageSummary(14),
      ]);
      syncFromAiTables({ providers, personas: allPersonas });
      setUsageSummary(usageData || { days: 14, timeline: [], totals: {} });
      setSnackbar({ open: true, message: "Provider configs saved. Keys rotated if provided.", severity: "success" });
      return true;
    } catch (e) {
      setSnackbar({ open: true, message: errorMessage(e) || "Failed to save provider config", severity: "error" });
      return false;
    } finally {
      setSavingProvider(false);
    }
  };

  const handleDeleteProvider = async (providerKey) => {
    const selectedProviderKey = String(providerKey || "").trim().toLowerCase();
    if (!selectedProviderKey) return false;
    try {
      await servicesService.deleteAiProviderConfig(selectedProviderKey, "default");
      const [providers, allPersonas, usageData] = await Promise.all([
        servicesService.getAiProviderConfigs(),
        servicesService.getAiPersonas(),
        servicesService.getAiUsageSummary(14),
      ]);
      syncFromAiTables({ providers, personas: allPersonas });
      setUsageSummary(usageData || { days: 14, timeline: [], totals: {} });
      setSnackbar({ open: true, message: "Provider and related personas deleted.", severity: "success" });
      return true;
    } catch (e) {
      setSnackbar({ open: true, message: errorMessage(e) || "Failed to delete provider", severity: "error" });
      return false;
    }
  };

  const handleDeletePersona = async (personaId) => {
    try {
      await servicesService.deleteAiPersona(personaId);
      const [providers, allPersonas, usageData] = await Promise.all([
        servicesService.getAiProviderConfigs(),
        servicesService.getAiPersonas(),
        servicesService.getAiUsageSummary(14),
      ]);
      syncFromAiTables({ providers, personas: allPersonas });
      setUsageSummary(usageData || { days: 14, timeline: [], totals: {} });
      setSnackbar({ open: true, message: "Persona deleted", severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: errorMessage(e) || "Failed to delete persona", severity: "error" });
    }
  };

  const filteredPersonas = personas.filter(
    (p) => String(p.provider_key || "").toLowerCase() === String(aiForm.selected_provider_key || "openai").toLowerCase()
  );
  const activeProviderKey = String(aiForm.selected_provider_key || "openai").toLowerCase();
  const activeProviderLabel = activeProviderKey === "gemini" ? "Gemini" : "OpenAI";
  const isActiveProviderConfigured = activeProviderKey === "gemini" ? providerMeta.gemini.hasKey : providerMeta.openai.hasKey;
  const selectedProviderConfigId = activeProviderKey === "gemini" ? providerMeta.gemini.id : providerMeta.openai.id;

  if (loading) {
    return (
      <Box sx={{ maxWidth: 700 }}>
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", px: { xs: 2, md: 3 }, py: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 1200 }}>
        <Routes>
        <Route
          index
          element={(
            <AiPersonaListPage
              activeProviderLabel={activeProviderLabel}
              isActiveProviderConfigured={isActiveProviderConfigured}
              aiForm={aiForm}
              setAiForm={setAiForm}
              providerMeta={providerMeta}
              businessSetup={businessSetup}
              hasRequiredBusinessSetup={hasRequiredBusinessSetup}
              businessAddressText={businessAddressText}
              handleSaveProviders={handleSaveProviders}
              handleDeleteProvider={handleDeleteProvider}
              savingProvider={savingProvider}
              filteredPersonas={filteredPersonas}
              setPersonaToDelete={setPersonaToDelete}
              usageSummary={usageSummary}
            />
          )}
        />
        <Route
          path="personas/new"
          element={(
            <AiPersonaFormPage
              mode="create"
              activeProviderLabel={activeProviderLabel}
              selectedProviderConfigId={selectedProviderConfigId}
              filteredPersonas={filteredPersonas}
              setSnackbar={setSnackbar}
              syncFromAiTables={syncFromAiTables}
            />
          )}
        />
        <Route
          path="personas/:personaId/edit"
          element={(
            <AiPersonaFormPage
              mode="edit"
              activeProviderLabel={activeProviderLabel}
              selectedProviderConfigId={selectedProviderConfigId}
              filteredPersonas={filteredPersonas}
              setSnackbar={setSnackbar}
              syncFromAiTables={syncFromAiTables}
            />
          )}
        />
        <Route path="*" element={<Navigate to="/ai-chatbot" replace />} />
        </Routes>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      <Dialog open={!!personaToDelete} onClose={() => setPersonaToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete persona preset?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {personaToDelete
                ? `Are you sure you want to delete "${personaToDelete.title}" (${personaToDelete.persona_key || personaToDelete.id})?`
                : "Are you sure you want to delete this persona?"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPersonaToDelete(null)}>Cancel</Button>
            <Button
              color="error"
              onClick={async () => {
                const id = personaToDelete?.id;
                setPersonaToDelete(null);
                if (id) await handleDeletePersona(id);
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
